/**
 * Created by rathompson on 5/23/22
 * File: AssociateAppModal
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */
import _ from 'lodash'
import { error } from '@panwds/tailwindcss'
import { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react'
import {
  Modal,
  ModalHeader,
  ModalFooter,
  Button,
  Bold,
  LoadingIcon,
  Tooltip,
  Select,
  Input,
  Body,
  Radio,
  RadioGroup
} from '@panwds/react-ui'
import { BUTTON_APPLY_LICENSES, BUTTON_CANCEL, BUTTON_SAVE, EMPTY_LICENSE, FIREWALLS_ASSOCIATION_LIMIT, FIREWALLS_LICENSES_LIMIT, LICENSE_FETCH_FAILED, LOADING_LABEL, SELECT_APP_TO_CONTINUE, SELECT_FIREWALLS, SELECT_FIREWALL_LICENSE_DESCRIPTION, SELECT_LICENSE, TITLE_ASSOCIATE_APPS } from '../../../constants/LabelConstants'
import styled from 'styled-components'
import { ARRAY, OBJECT } from '../../../../common/utils/Utils'
import SubscriptionService from '../../../../subscription/services/SubscriptionService'
import AssociateAppTable from './table/AssociateAppTable'
import AssociateAppsSidebar from './table/AssociateAppsSidebar'
import ZIndex from '../../../../common/components/zIndex/ZIndex'
import AppContext from '../../../../common/context/AppContext'
import { selectedAppDef, buildFirewallLicenseGrouping, constructSelectItems, filterDevicesWithLicenseGroup, filterDevicesWithRegex, isFirewallRequireLicense, hasSelectedAppHasLicenseFlavor, hasSubLicenseFirewallRequireLicense, buildSkuCompatibleDevicelist } from './associationTableUtils'
import DividerLine from '../../../../common/components/dividerLine/DividerLine'
import AuthContext from '../../../../common/context/AuthContext'
import { ACTIVATION_TYPE } from '../../../../common/constants/GlobalConstants'

const CONTAINER = styled.div`
  position: absolute;
  inset: 0 0 0 0;
  display: flex;
  flex-direction: column;
  flex: 1 1;

  .tw-border-solid.tw-border-gray-200.tw-border-0.tw-flex.tw-relative.tw-bg-white.tw-border.tw-overflow-auto {
    flex: 1 1;
  }

  button[id^=table] {
    color: #333333;
  }

  button[aria-label="Previous"] {
    color: #333333;
  }

  button[aria-label="Next"] {
    color: #333333;
  }

  .tw-flex.tw-bg-white {
    flex: 1 1;
  }

  input[type=checkbox] {
    min-height: 1rem;
    min-width: 1rem;
    font-family: inherit !important;
    position: initial !important;
    -moz-appearance: auto !important;
    -webkit-appearance: checkbox !important;
    -o-appearance: auto !important;

    &[disabled] {
      background-image: none;
      border-width: 1px;
      border-style: solid;
      border-radius: 0.063rem;
      --tw-border-opacity: 1;
      border-color: rgba(218,219,219,var(--tw-border-opacity));
      background-color: transparent;
    }
  }
`

const AssociateAppModal = ({
  isOpen,
  closeModal,
  tenant = {},
  fetchDevices = async () => [],
  callback = (data) => {
  },
  devices = [],
  products = [],
  fetchAppFirewallLicenses: _fetchAppFirewallLicenses = async () => [],
  setNewDevices = () => null,
  fetchSkuCompatibleDevices: _fetchSkuCompatibleDevices = async () => []
}) => {

  const { apps } = useContext(AppContext)
  const { user } = useContext(AuthContext)

  const [loading, setLoading] = useState(false)
  const [activationError, setActivationError] = useState(null)

  const [selectedApp, setSelectedApp] = useState(null)
  const [deviceList, setDeviceList] = useState([])
  const [selected, setSelected] = useState([])

  const [licenseModelTypeTermMap, setLicenseModelTypeTermMap] = useState(null)
  const [selectLicenseItems, setSelectLicenseItems] = useState([])
  const [licenseSelected, setLicenseSelected] = useState(null)
  const [hasFirewallLicense, setHasFirewallLicense] = useState(false)
  const [licenseLoader, setLicenseLoader] = useState(false)
  const [selectedBaseLicense, setSelectedBaseLicense] = useState(null)
  const [hasBaseLicense, setHasBaseLicense] = useState(false)
  const [skuCompatibleDeviceMap, setSkuCompatibleDeviceMap] = useState([])

  const tableRef = useRef()
  const isFirewallSelectionExceeded = selected.length > 25
  const resetPaidAppsLocalStates = () => {
    setDeviceList([]) //reset devices list
    setLicenseModelTypeTermMap(null)
    setSelectLicenseItems([])
    setLicenseSelected(null)
    setLicenseLoader(false)
    setActivationError(null)
  }

  const resetFreeAppsLocalStates = () => {
    setDeviceList([]) //reset devices list
    setSelected([])
  }
  const clearAllLocalStates = useCallback(() => {
    resetPaidAppsLocalStates()
    resetFreeAppsLocalStates()
  }, [])

  const fetchAppFirewallLicenses = useCallback(async (selectedApp, use_device_filter_api) => {
    setLicenseModelTypeTermMap(null) // reset app fw licenses
    setLicenseLoader(true)
    const response = await _fetchAppFirewallLicenses(selectedApp).catch(error => {
      // eslint-disable-next-line no-console
      // console.log(error)
      setActivationError(LICENSE_FETCH_FAILED)
    })
    const appFwLicenseGroup = buildFirewallLicenseGrouping(response)
    let skuMap = new Map()
    if (use_device_filter_api) {
      const _keys = [...appFwLicenseGroup.keys()]
      if (_keys) {
        const skus = []
        _keys.forEach(key => {
          skus.push(appFwLicenseGroup.get(key)?.[0]?.license_sku)
        })
        const sku_response = await _fetchSkuCompatibleDevices(skus.join(',')).catch(error => {
          // eslint-disable-next-line no-console
          // console.log(error)
          setActivationError(LICENSE_FETCH_FAILED)
        })
        skuMap = buildSkuCompatibleDevicelist(sku_response)
      }
    }
    setSkuCompatibleDeviceMap(skuMap)
    setLicenseModelTypeTermMap(appFwLicenseGroup) // set new app fw licenses
    setLicenseLoader(false)
  }, [_fetchAppFirewallLicenses, _fetchSkuCompatibleDevices])

  /**
   * Will revisit this code if there is performace issue while fetching all compatible sku devices for all the license
   */
  // const fetchSkuCompatibleDevices = useCallback(async ({ appDef, selectedApp, devices, licenseModelTypeTermMap, currentSelectedLicense }) => {
  //   setLicenseLoader(true)
  //   let skuMap = new Map()
  //   const selectedSku = licenseModelTypeTermMap?.get(currentSelectedLicense.id)?.[0]?.license_sku
  //   const response = await _fetchSkuCompatibleDevices(selectedSku).catch(error => {
  //     // eslint-disable-next-line no-console
  //     // console.log(error)
  //     setActivationError(LICENSE_FETCH_FAILED)
  //   })
  //   skuMap = buildSkuCompatibleDevicelist(response)
  //   const filteredDevices = filterDevicesWithLicenseGroup({
  //     appDef,
  //     selectedApp,
  //     devices,
  //     licenseModelTypeTermMap,
  //     currentSelectedLicense,
  //     skuMap
  //   })
  //   setSkuCompatibleDeviceMap(skuMap)
  //   setDeviceList(filteredDevices)
  //   setLicenseLoader(false)
  // }, [_fetchSkuCompatibleDevices])

  const userAccounts = useMemo(() => user?.supportAccountIds || [], [user])
  const getSupportAccountSignature = useCallback((support_account_id) => {
    return userAccounts.find(e => e.accountid === support_account_id)?.signature
  }, [userAccounts])

  useEffect(() => {
    setNewDevices([])
  }, [setNewDevices])

  useEffect(() => {
    const _hasFirewallLicense = isFirewallRequireLicense(apps, selectedApp) && hasSubLicenseFirewallRequireLicense(apps, selectedApp, selectedBaseLicense)
    const use_device_filter_api = selectedAppDef(apps, selectedApp)?.device_association?.use_device_matrix_api
    setHasFirewallLicense(_hasFirewallLicense)
    if (selectedApp && _hasFirewallLicense) { // paid apps
      clearAllLocalStates()
      fetchAppFirewallLicenses(selectedApp, use_device_filter_api)
    }

    else if (selectedApp) { // free apps
      clearAllLocalStates()
      const appDeviceRegex = selectedAppDef(apps, selectedApp)?.device_association?.model_regex
      const filteredDevices = appDeviceRegex ? filterDevicesWithRegex(appDeviceRegex, devices) : devices
      setDeviceList(filteredDevices)
    }
  }, [selectedApp, apps, devices, fetchAppFirewallLicenses, clearAllLocalStates, selectedBaseLicense])

  useEffect(() => {
    if (!_.isEmpty(licenseModelTypeTermMap)) {
      const appDef = selectedAppDef(apps, selectedApp)
      const items = constructSelectItems({ licenseModelTypeTermMap, appDef })
      const defaultSelectedItem = items?.[0]
      setSelectLicenseItems(items)
      setLicenseSelected(defaultSelectedItem)
      const filteredDevices = filterDevicesWithLicenseGroup({
        appDef,
        selectedApp,
        devices,
        licenseModelTypeTermMap,
        currentSelectedLicense: defaultSelectedItem,
        skuCompatibleDeviceMap
      })
      setDeviceList(filteredDevices)
    }
  }, [licenseModelTypeTermMap, apps, devices, selectedApp, skuCompatibleDeviceMap])

  useEffect(() => {
    const _hasSelectedAppHasLicenseFlavor = hasSelectedAppHasLicenseFlavor(apps, selectedApp)
    if (!_hasSelectedAppHasLicenseFlavor) {
      setSelectedBaseLicense(null)
      setHasBaseLicense(false)
    }
    else {
      setHasBaseLicense(_hasSelectedAppHasLicenseFlavor)
    }
  }, [apps, selectedApp])

  const onSave = useCallback(async () => {
    try {
      const clonedAvailableLicenses = _.cloneDeep(hasFirewallLicense ? licenseModelTypeTermMap?.get(licenseSelected?.id) : [])
      const findAndSetLicenseId = ({ model, serial_number }) => {
        const unUsedFirewallLicense = clonedAvailableLicenses.find(each => {
          if (each.isModelAgnostic) {
            return !Boolean(each.is_used)
          }
          else {
            //TODO IOT-VM: needed for IoT flows and remove VM once VM flex is released
            return (each.license_sku_model === model || (each.license_sku_model === model?.match(/VM-(\d+)/)?.[0]) || (each.license_sku_model === model?.match(/M-(\d+)/)?.[0])) && !Boolean(each.is_used)
          }
        })
        if (!unUsedFirewallLicense?.isEla) {
          unUsedFirewallLicense.is_used = true // mark it as used only for non-ela, as ela uses same license for all devices
        }
        return unUsedFirewallLicense?.license_id
      }

      const support_account_id = selectedApp?.support_account_id
      const signature = getSupportAccountSignature(+support_account_id)
      const notonorafter = user?.notonorafter
      const x_sig = `${support_account_id} ${notonorafter} ${signature}`

      setLoading(true)
      const associations = []
      for (const { serial_number, model, isUpgrade } of selected) {
        if (OBJECT.hasObject(selectedApp)) {
          const { tenant_id } = selectedApp
          associations.push({
            serial_number,
            tenant_id,
            ...(isUpgrade && { activation_type: ACTIVATION_TYPE.AMEND }),
            ...(hasFirewallLicense && { license_id: findAndSetLicenseId({ model, serial_number }) })
          })
        }
      }
      const response = await SubscriptionService.devices.associateApps({
        tsg_id: tenant?.id,
        associations,
        x_sig,
        is_eula_accepted: true // hard coded for now
      })
      const { errors: { failed_count = 0, session_id } = {} } = response.data || {}
      const result = {
        deviceSuccessCount: selected.length - failed_count, // for one app response will have dependent services mapping too eg: buckbeak association
        deviceFailureCount: failed_count,
        ...(session_id && { errorSessionId: session_id })
      }
      await fetchDevices()
      closeModal()
      callback({ result })
    }
    catch (error) {
      const { session_id, message } = error
      const constructSessionMsg = () => `LIC_400 Failed to apply license on devices. Please include this Error id: ${session_id} in support ticket`
      const errorMsg = session_id ? constructSessionMsg() : message
      setActivationError(errorMsg)
      // createMessage('error', errorMsg, 30000) //30 secs
    }
    setLoading(false)
  }, [callback, closeModal, fetchDevices, getSupportAccountSignature, hasFirewallLicense, licenseModelTypeTermMap, licenseSelected?.id, selected, selectedApp, tenant, user?.notonorafter])

  const isFirewallLicenseSelectionExceeded = useMemo(() => {
    const availableFirewallLicenses = licenseModelTypeTermMap?.get(licenseSelected?.id)
    const isElaLicenseSelected = availableFirewallLicenses?.every(e => Boolean(e?.isEla))
    if (hasFirewallLicense && licenseSelected && !isElaLicenseSelected) { // paid apps and not ela
      const firewallSelectedCount = selected.length
      const availableFirewallLicensesCount = availableFirewallLicenses?.length
      return firewallSelectedCount > availableFirewallLicensesCount
    }
    return false // free apps

  }, [licenseModelTypeTermMap, hasFirewallLicense, licenseSelected, selected.length])

  const submitDisabled = useMemo(() => {
    return !Boolean(selectedApp) || !ARRAY.hasArray(selected) || isFirewallSelectionExceeded || isFirewallLicenseSelectionExceeded
  }, [isFirewallLicenseSelectionExceeded, isFirewallSelectionExceeded, selected, selectedApp])

  const submitTooltipLabel = useMemo(() => {
    if (!Boolean(selectedApp)) {
      return SELECT_APP_TO_CONTINUE
    }
    else if (isFirewallSelectionExceeded) {
      return FIREWALLS_ASSOCIATION_LIMIT
    }
    else if (isFirewallLicenseSelectionExceeded) {
      return FIREWALLS_LICENSES_LIMIT
    }
    else {
      return SELECT_FIREWALLS
    }
  }, [isFirewallLicenseSelectionExceeded, isFirewallSelectionExceeded, selectedApp])

  const handleSelectOnLicenseGroupChange = (props) => {
    setLicenseSelected(props.selectedItem)
    const appDef = selectedAppDef(apps, selectedApp)
    const filteredDevices = filterDevicesWithLicenseGroup({
      appDef,
      selectedApp,
      devices,
      licenseModelTypeTermMap,
      currentSelectedLicense: props.selectedItem,
      skuCompatibleDeviceMap: skuCompatibleDeviceMap
    })
    setDeviceList(filteredDevices)
  }

  const renderLicensesSelect = () => {
    return <div className="tw-flex tw-flex-col tw-my-2">
      <Bold appearance="semibold">{SELECT_LICENSE}</Bold>
      <DividerLine />
      <Bold appearance="semibold" className='tw-text-xs tw-my-2'>{SELECT_FIREWALL_LICENSE_DESCRIPTION}</Bold>
      <Select
        addClassName='cmm-modal-select'
        customWidth='28rem'
        dataTestId={'das-app-device-license'}
        isLoading={licenseLoader}
        placeholder={licenseLoader ? LOADING_LABEL : (!selectLicenseItems.length ? EMPTY_LICENSE : '')}
        // fitToItemWidth // not working, creating flicker menu when items are more than 21+ without enableSearch
        listItemSize='md'
        items={selectLicenseItems}
        selectedItem={licenseSelected}
        onChange={handleSelectOnLicenseGroupChange}
        menuStyle={{
          width: '28rem'
        }}
        enableSearch
      />
    </div>
  }

  const submitButtonLabel = useMemo(() => (hasFirewallLicense ? BUTTON_APPLY_LICENSES : BUTTON_SAVE), [hasFirewallLicense])

  const renderLicenseFlavorSelect = () => {
    return <div className="tw-flex tw-flex-col tw-my-2">
      <Bold appearance="semibold">Select Base license type</Bold>
      <DividerLine />
      <RadioGroup
        horizontal
        name="lice_flavor"
        onChange={event => {
          setSelectedBaseLicense(event.target.value)
        }}
      >
        <Radio
          key={1}
          value={'SDWAN'}
          disabled={false}
          checked={selectedBaseLicense === 'SDWAN'}
        >
          <Body addClassName={'tw-text-secondary'}>{'SDWAN'}</Body>
        </Radio>
        <Radio
          key={2}
          value={'GP-SdWAN'}
          disabled={false}
          checked={selectedBaseLicense === 'GP-SdWAN'}
        >
          <Body addClassName={'tw-text-secondary'}>{'GP-SDWAN'}</Body>
        </Radio> <Radio
          key={3}
          value={'GP'}
          disabled={false}
          checked={selectedBaseLicense === 'GP'}
        >
          <Body addClassName={'tw-text-secondary'}>{'GP'}</Body>
        </Radio>
      </RadioGroup>
    </div>
  }
  return (
    <ZIndex>
      <Modal
        style={{ top: '50%', '--tw-translate-y': '-50%', minHeight: '800px', maxHeight: '800px' }}
        size="xl"
        onClose={closeModal}
        {...{ isOpen }}
      >
        <ModalHeader
          title={TITLE_ASSOCIATE_APPS}
        />
        {Boolean(activationError) &&
          <Input
            readOnly={true}
            disabled={true}
            showLabel={false}
            value={activationError}
            allowCopy={true}
            style={{ color: error.DEFAULT }}
            name='dasFirewallActivate'
            data-testid='das-firewall-activate-error'
            data-metrics='das-firewall-activate-error'
          />
        }
        <div className='tw-relative tw-flex tw-flex-1 tw-items-stretch'>
          {loading && (
            <div
              className="tw-flex tw-absolute tw-inset-0 tw-justify-center tw-items-center tw-z-50 tw-bg-gray tw-opacity-60">
              <LoadingIcon size="4xl" />
            </div>
          )}
          <AssociateAppsSidebar {...{ products, setSelectedApp }} />
          <div className="tw-relative tw-flex tw-flex-col tw-p-4 tw-box-border tw-font-lato tw-flex-1 tw-overflow-hidden">
            <CONTAINER>
              {hasBaseLicense && renderLicenseFlavorSelect()}
              {hasFirewallLicense && renderLicensesSelect()}

              <AssociateAppTable
                data={deviceList}
                tableRef={tableRef}
                setSelected={setSelected}
                selectedApp={selectedApp}
                hasFirewallLicense={hasFirewallLicense}
                licenseLoader={licenseLoader}
                error={activationError}
              />
            </CONTAINER>
          </div>
        </div>
        <ModalFooter>
          <Button
            appearance="secondary"
            disabled={loading}
            onClick={closeModal}>
            {BUTTON_CANCEL}
          </Button>
          {submitDisabled ? (
            <Tooltip label={submitTooltipLabel}>
              <Button
                appearance="primary"
                disabled={submitDisabled}
                onClick={onSave}>
                {submitButtonLabel}
              </Button>
            </Tooltip>
          ) : (
            <Button
              appearance="primary"
              disabled={loading}
              onClick={onSave}>
              {submitButtonLabel}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </ZIndex>
  )
}

AssociateAppModal.propTypes = {}
export default AssociateAppModal
